import { useState } from 'react';
import { Layers, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/crm-ui';
import { useCreateWorkspace } from '@/hooks/useWorkspaces';

export function CreateWorkspaceDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const createWorkspace = useCreateWorkspace();
  const trimmed = name.trim();
  const busy = createWorkspace.isPending;

  const submit = async () => {
    if (!trimmed || busy) return;
    try {
      const ws = await createWorkspace.mutateAsync({ name: trimmed });
      onCreated?.(ws.id);
      setName('');
      onOpenChange(false);
    } catch {
    }
  };

  const close = () => {
    if (busy) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setName('');
        onOpenChange(v);
      }}
    >
      <DialogContent
        hideClose
        className="w-[calc(100%-32px)] max-w-[480px] sm:w-full gap-0 overflow-hidden rounded-2xl border-0 p-0 shadow-[0_20px_60px_rgba(16,24,40,0.2)]"
      >
        <div className="relative overflow-hidden bg-gradient-to-b from-[#DBEAFE] via-brand-soft to-white px-6 pb-[5px] pt-6">
          <button
            type="button"
            aria-label="Close"
            disabled={busy}
            onClick={close}
            className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/70 text-[#475467] transition hover:bg-white disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>

          <div className="flex items-center justify-center pt-2">
            <div className="flex h-[60px] w-[60px] items-center justify-center rounded-[12.5px] border border-[#EDF1F7] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06)]">
              <Layers className="h-6 w-6 text-brand" />
            </div>
          </div>

          <p className="mt-4 text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-[#667085]">
            Workspace
          </p>
          <DialogTitle className="mt-[5px] text-center text-[20px] font-semibold tracking-[-0.01em] text-[#101828]">
            Create a workspace
          </DialogTitle>
        </div>

        <div className="px-6 pb-4">
          <DialogDescription className="text-center text-[13px] leading-relaxed text-[#667085]">
            Its own conversations, contacts, campaigns and reports. Connect WhatsApp,
            Instagram, Facebook or any other channel whenever you are ready.
          </DialogDescription>

          <label
            htmlFor="create-workspace-name"
            className="mt-5 block text-[12.5px] font-semibold text-[#344054]"
          >
            Workspace name
          </label>
          <Input
            id="create-workspace-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            placeholder="e.g. Marketing, Client X"
            maxLength={255}
            disabled={busy}
            className="mt-1.5 h-10 rounded-lg border-[#D0D5DD] text-[13.5px]"
            data-testid="create-workspace-name"
          />
        </div>

        <div className="px-6 pb-6 pt-1">
          <Button
            variant="primary"
            className="w-full"
            onClick={submit}
            disabled={!trimmed || busy}
            data-testid="create-workspace-submit"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? 'Creating…' : 'Create workspace'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
